import axios from 'axios';
import { getConfiguredApiUrl } from '../config/runtimeConfig';
import {
  clearStoredSession,
  getStoredRefreshToken,
  getStoredToken,
  getStoredUser,
  saveStoredTokens,
  saveStoredUser,
} from '../utils/session';

export const getApiBaseUrl = () => {
  const envUrl = getConfiguredApiUrl();
  if (envUrl) {
    return envUrl.replace(/\/?$/, '');
  }

  const { protocol, hostname, port } = window.location;
  const origin = `${protocol}//${hostname}${port ? `:${port}` : ''}`;

  // local CRA dev uses proxy or same origin; backend is usually under /api
  return `${origin}/api`;
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise: Promise<string | null> | null = null;

const clearSessionAndRedirect = () => {
  // No logout call here: we only land in this path because the refresh token was already rejected.
  clearStoredSession();
  window.location.href = '/login';
};

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  try {
    const response = await axios.post<{
      token: string;
      refreshToken: string;
      refreshTokenExpiresAt: string;
    }>(`${API_BASE_URL}/auth/refresh-token`, { refreshToken }, {
      headers: { 'Content-Type': 'application/json' },
    });

    const { token, refreshToken: nextRefreshToken, refreshTokenExpiresAt } = response.data;
    // Not user activity: a background call refreshing the token must not extend the idle window.
    saveStoredTokens(token, nextRefreshToken);

    const userStr = getStoredUser();
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        const nextUser = {
          ...user,
          token,
          refreshToken: nextRefreshToken,
          refreshTokenExpiresAt,
        };
        saveStoredUser(nextUser);
      } catch {}
    }

    return token;
  } catch {
    return null;
  }
};

// Request interceptor to attach JWT token
api.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach tenantId and organizationId from user (if available)
    const userStr = getStoredUser();
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.tenantId) {
          config.headers['X-Tenant-Id'] = user.tenantId;
        }
        if (user.organizationId) {
          config.headers['X-Organization-Id'] = user.organizationId;
        }
      } catch {}
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl = String(error.config?.url || '');
    const isLoginRequest = /\/auth\/login$/i.test(requestUrl);
    const isRefreshRequest = /\/auth\/refresh-token$/i.test(requestUrl);
    const isLogoutRequest = /\/auth\/logout$/i.test(requestUrl);

    if (error.response?.status === 401 && !isLoginRequest && !isRefreshRequest && !isLogoutRequest && !error.config?._retry) {
      error.config._retry = true;
      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });

      const newToken = await refreshPromise;
      if (newToken) {
        error.config.headers = error.config.headers ?? {};
        error.config.headers.Authorization = `Bearer ${newToken}`;
        return api(error.config);
      }

      clearSessionAndRedirect();
    }
    return Promise.reject(error);
  }
);

export default api;
